<template>
  <transition name="modal-fade">
    <div v-if="isOpen" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>
            <template v-if="editMode">
              <IconSvg name="pen-solid-full" size="md" />
              {{ itemType === "BELONGINGS" ? "持ち物編集" : "To Do編集" }}
            </template>
            <template v-else>
              <IconSvg
                :name="
                  itemType === 'BELONGINGS'
                    ? 'bag-shopping-solid-full'
                    : 'check-solid-full'
                "
                size="md"
              />
              {{ itemType === "BELONGINGS" ? "持ち物追加" : "To Do追加" }}
            </template>
          </h2>
          <button class="close-button" @click="closeModal">×</button>
        </div>

        <!-- エラーメッセージ -->
        <div v-if="errorMessage" class="error-message">
          {{ errorMessage }}
        </div>

        <form
          @submit.prevent="handleSubmit"
          @keydown.ctrl.enter.prevent="handleSubmit"
          class="modal-body"
        >
          <!-- 項目名 -->
          <div class="form-group">
            <label for="itemName">項目名 <span class="required">*</span></label>
            <input
              type="text"
              id="itemName"
              v-model="formData.itemName"
              :placeholder="
                itemType === 'BELONGINGS'
                  ? '例: パスポート、充電器、常備薬'
                  : '例: パスポートの有効期限確認'
              "
              maxlength="255"
              required
            />
          </div>

          <!-- カテゴリ名 -->
          <div class="form-group">
            <label for="categoryName">カテゴリ</label>
            <div class="combobox-wrapper">
              <input
                id="categoryName"
                type="text"
                v-model="formData.categoryName"
                :placeholder="
                  itemType === 'BELONGINGS'
                    ? '例: 衣類、電子機器、書類'
                    : '例: 準備、手続き、予約'
                "
                maxlength="255"
                autocomplete="off"
                @focus="showCategoryDropdown = true"
                @blur="hideCategoryDropdown"
              />
              <ul
                v-if="showCategoryDropdown && filteredCategories.length > 0"
                class="combobox-dropdown"
              >
                <li
                  v-for="cat in filteredCategories"
                  :key="cat"
                  @mousedown.prevent="selectCategory(cat)"
                >
                  {{ cat }}
                </li>
              </ul>
            </div>
          </div>

          <!-- 担当者（TODOは複数選択） -->
          <div v-if="itemType === 'TODO'" class="form-group">
            <label>担当者</label>
            <div class="member-select-list">
              <label
                v-for="member in shioriMembers"
                :key="member.MEMBER_CODE"
                class="member-check-row"
                :class="{
                  'member-row-disabled':
                    formData.isPrivate &&
                    member.APP_USER_CODE !== currentUserCode,
                }"
              >
                <input
                  type="checkbox"
                  :value="member.MEMBER_CODE"
                  v-model="formData.selectedMembers"
                  :disabled="
                    formData.isPrivate &&
                    member.APP_USER_CODE !== currentUserCode
                  "
                />
                <span class="member-check-name">{{ member.MEMBER_NAME }}</span>
              </label>
              <p v-if="shioriMembers.length === 0" class="no-member-note">
                メンバーがいません
              </p>
            </div>
          </div>

          <!-- 期限 -->
          <div v-if="itemType === 'TODO'" class="form-group">
            <label for="deadline">期限</label>
            <input
              type="date"
              id="deadline"
              v-model="formData.deadline"
              min="1900-01-01"
              max="9999-12-31"
              @input="validateYear"
              @change="validateYear"
              @blur="validateYear"
            />
          </div>

          <!-- 優先度 -->
          <div v-if="itemType === 'TODO'" class="form-group">
            <label for="priority">優先度</label>
            <select id="priority" v-model="formData.priorityLevel">
              <option value="LOW">低</option>
              <option value="NORMAL">通常</option>
              <option value="HIGH">高</option>
            </select>
          </div>

          <!-- メンバー（持ち物のみ） -->
          <div v-if="itemType === 'BELONGINGS'" class="form-group">
            <label>持って行くメンバー</label>
            <div class="member-select-list">
              <label
                v-for="member in shioriMembers"
                :key="member.MEMBER_CODE"
                class="member-check-row"
                :class="{
                  'member-row-disabled':
                    formData.isPrivate &&
                    member.APP_USER_CODE !== currentUserCode,
                }"
              >
                <input
                  type="checkbox"
                  :value="member.MEMBER_CODE"
                  v-model="formData.selectedMembers"
                  :disabled="
                    formData.isPrivate &&
                    member.APP_USER_CODE !== currentUserCode
                  "
                />
                <span class="member-check-name">{{ member.MEMBER_NAME }}</span>
                <span
                  v-if="
                    formData.isPrivate &&
                    member.APP_USER_CODE !== currentUserCode
                  "
                  class="member-private-note"
                  >非公開のため不可</span
                >
              </label>
              <p v-if="shioriMembers.length === 0" class="no-member-note">
                メンバーがいません
              </p>
            </div>
          </div>

          <!-- アラート通知 -->
          <div
            v-if="itemType === 'TODO' && !isSharedEditing"
            class="form-group checkbox-group"
          >
            <label>
              <input type="checkbox" v-model="formData.deadlineNotify" />
              期限が近づいたら通知する
            </label>
          </div>

          <!-- 非公開設定 -->
          <div
            v-if="!isSharedEditing"
            class="form-group checkbox-group private-group"
          >
            <label class="private-label">
              <input type="checkbox" v-model="formData.isPrivate" />
              <span class="private-label-text">
                <IconSvg
                  name="lock-solid-full"
                  size="sm"
                  class="private-icon"
                />
                自分だけに表示する（他のメンバーには見えません）
              </span>
            </label>
          </div>

          <div v-if="itemType === 'TODO'" class="form-group memo-group">
            <label for="memoText">メモ</label>
            <textarea
              id="memoText"
              v-model="formData.memoText"
              rows="4"
              maxlength="1000"
              placeholder="補足、確認事項、URLなどを入力"
            ></textarea>
            <p class="field-help">{{ formData.memoText.length }}/1000文字</p>
          </div>
        </form>

        <div class="modal-footer">
          <button
            type="button"
            class="cancel-button"
            @click="closeModal"
            :disabled="isSubmitting"
          >
            キャンセル
          </button>
          <button
            type="button"
            class="submit-button"
            @click="handleSubmit"
            :disabled="isSubmitting"
          >
            {{ isSubmitting ? "処理中..." : editMode ? "更新" : "作成" }}
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { ref, computed, watch } from "vue";
import { useChecklistStore } from "@/store/checklist-store";
import { useShioriMemberStore } from "@/store/shiori-member-store";
import { usechecklistDetailMemberAccountStore } from "@/store/checklist-detail-member-store";
import { useAuthStore } from "@/store/authStore";
import { useChecklistTemplateStore } from "@/store/checklist-template-store";
import IconSvg from "@/components/IconSvg.vue";

export default {
  name: "CreateChecklistModal",
  components: { IconSvg },
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    shioriId: {
      type: String,
      required: true,
    },
    editMode: {
      type: Boolean,
      default: false,
    },
    copyMode: {
      type: Boolean,
      default: false,
    },
    checklistData: {
      type: Object,
      default: null,
    },
    scheduleData: {
      type: Object,
      default: null,
    },
    itemType: {
      type: String,
      default: "TODO",
    },
    shareToken: {
      type: String,
      default: "",
    },
    members: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["close", "create", "update", "access-denied"],
  setup(props, { emit }) {
    const checklistStore = useChecklistStore();
    const shioriMemberStore = useShioriMemberStore();
    const memberStore = usechecklistDetailMemberAccountStore();
    const authStore = useAuthStore();
    const templateStore = useChecklistTemplateStore();
    const isSharedEditing = computed(() => Boolean(props.shareToken));

    // 現在ログイン中ユーザーの APP_USER_CODE
    const currentUserCode = computed(
      () => authStore.currentUser?.APP_USER_CODE || "",
    );

    const existingCategories = computed(() => {
      const checklists =
        checklistStore.getChecklistsBySioriId(props.shioriId) || [];
      const cats = new Set();
      checklists.forEach((item) => {
        if (
          (item.ITEM_TYPE || "TODO") === props.itemType &&
          item.CATEGORY_NAME
        ) {
          cats.add(item.CATEGORY_NAME);
        }
      });
      // テンプレートのカテゴリも候補に含める
      if (props.itemType === "BELONGINGS") {
        templateStore.items.forEach((item) => {
          if (item.CATEGORY_NAME) cats.add(item.CATEGORY_NAME);
        });
      }
      return Array.from(cats).sort();
    });

    const formData = ref({
      itemName: "",
      memoText: "",
      categoryName: "",
      assignedMemberCode: "",
      deadline: "",
      priorityLevel: "NORMAL",
      deadlineNotify: false,
      selectedMembers: [],
      isPrivate: false,
    });

    const errorMessage = ref("");
    const isSubmitting = ref(false);

    // しおりメンバー取得
    const shioriMembers = computed(() => {
      return props.members && props.members.length > 0
        ? props.members
        : shioriMemberStore.members[props.shioriId] || [];
    });

    // 非公開にした時、自分以外のメンバーのチェックを外す
    watch(
      () => formData.value.isPrivate,
      (isPrivate) => {
        if (isPrivate && !isSharedEditing.value) {
          formData.value.selectedMembers =
            formData.value.selectedMembers.filter((code) => {
              const m = shioriMembers.value.find((m) => m.MEMBER_CODE === code);
              return m?.APP_USER_CODE === currentUserCode.value;
            });
        }
      },
    );

    const showCategoryDropdown = ref(false);

    const filteredCategories = computed(() => {
      const input = (formData.value.categoryName || "").trim().toLowerCase();
      if (!input) return existingCategories.value;
      return existingCategories.value.filter((cat) =>
        cat.toLowerCase().includes(input),
      );
    });

    const selectCategory = (cat) => {
      formData.value.categoryName = cat;
      showCategoryDropdown.value = false;
    };

    const hideCategoryDropdown = () => {
      setTimeout(() => {
        showCategoryDropdown.value = false;
      }, 150);
    };

    const closeModal = () => {
      emit("close");
      setTimeout(() => {
        resetForm();
      }, 300);
    };

    const resetForm = () => {
      formData.value = {
        itemName: "",
        memoText: "",
        categoryName: "",
        assignedMemberCode: "",
        deadline: "",
        priorityLevel: "NORMAL",
        deadlineNotify: false,
        selectedMembers: [],
        isPrivate: false,
      };
      showCategoryDropdown.value = false;
      errorMessage.value = "";
    };

    const handleSubmit = async () => {
      isSubmitting.value = true;
      errorMessage.value = "";

      try {
        if (props.editMode && props.checklistData) {
          // 編集モード
          const updateData = {
            itemName: formData.value.itemName,
            memoText:
              props.itemType === "TODO" ? formData.value.memoText : null,
            categoryName: formData.value.categoryName || null,
            deadline:
              props.itemType === "TODO" ? formData.value.deadline || null : null,
            assignedMemberCode: formData.value.selectedMembers[0] || null,
            assignedMemberCodes: formData.value.selectedMembers,
            priorityLevel:
              props.itemType === "TODO"
                ? formData.value.priorityLevel
                : undefined,
            deadlineNotifyFlg:
              !isSharedEditing.value &&
              props.itemType === "TODO" &&
              formData.value.deadlineNotify
                ? "1"
                : "0",
            itemType: props.itemType,
            privateFlg:
              !isSharedEditing.value && formData.value.isPrivate ? "1" : "0",
          };

          if (props.shareToken) {
            await checklistStore.updateSharedChecklist(
              props.shareToken,
              props.checklistData.PAGE_NO || "1",
              props.checklistData.CHECKLIST_NO,
              updateData,
            );
          } else {
            await checklistStore.updateChecklist(
              props.shioriId,
              props.checklistData.PAGE_NO,
              props.checklistData.CHECKLIST_NO,
              updateData,
            );
          }

          console.log("チェックリスト更新成功");
          emit("update", {
            pageNo: props.checklistData.PAGE_NO,
            checklistNo: props.checklistData.CHECKLIST_NO,
            updateData,
          });
        } else {
          // 新規作成モード
          const createData = {
            shioriId: props.shioriId,
            pageNo: "1",
            itemName: formData.value.itemName,
            memoText:
              props.itemType === "TODO" ? formData.value.memoText : null,
            categoryName: formData.value.categoryName || null,
            deadline:
              props.itemType === "TODO" ? formData.value.deadline || null : null,
            assignedMemberCode: formData.value.selectedMembers[0] || null,
            assignedMemberCodes: formData.value.selectedMembers,
            priorityLevel:
              props.itemType === "TODO"
                ? formData.value.priorityLevel
                : undefined,
            deadlineNotifyFlg:
              !isSharedEditing.value &&
              props.itemType === "TODO" &&
              formData.value.deadlineNotify
                ? "1"
                : "0",
            scheduleLineNon: props.scheduleData?.LINE_NON || null,
            itemType: props.itemType,
            privateFlg:
              !isSharedEditing.value && formData.value.isPrivate ? "1" : "0",
          };

          if (props.shareToken) {
            await checklistStore.createSharedChecklist(
              props.shareToken,
              createData,
            );
          } else {
            await checklistStore.createChecklist(createData);
          }

          // BELONGINGS の場合、選択メンバーを追加
          if (
            props.itemType === "__DISABLED_BELONGINGS__" &&
            formData.value.selectedMembers.length > 0
          ) {
            // createChecklist のレスポンスから CHECKLIST_NO を取得するため fetchChecklists 後に取得
            // データを再取得して最新の CHECKLIST_NO を使う
            const latestChecklists = checklistStore.getChecklistsBySioriId(
              props.shioriId,
            );
            const created = latestChecklists
              .filter(
                (c) =>
                  c.ITEM_NAME === createData.itemName &&
                  c.ITEM_TYPE === "BELONGINGS",
              )
              .sort(
                (a, b) => Number(b.CHECKLIST_NO) - Number(a.CHECKLIST_NO),
              )[0];

            if (created) {
              for (const memberCode of formData.value.selectedMembers) {
                try {
                  await memberStore.addMember(
                    props.shioriId,
                    created.PAGE_NO,
                    created.CHECKLIST_NO,
                    memberCode,
                  );
                } catch (e) {
                  // 既存メンバーは409で無視
                }
              }
            }
          }

          console.log("チェックリスト作成成功");
          emit("create");
        }

        closeModal();
      } catch (error) {
        console.error("チェックリスト処理エラー:", error);
        if (
          props.shareToken &&
          [403, 404].includes(error.response?.status)
        ) {
          emit("access-denied", error);
        }
        errorMessage.value =
          error.response?.data?.detail ||
          (props.editMode
            ? "チェックリストの更新に失敗しました"
            : "チェックリストの作成に失敗しました");
      } finally {
        isSubmitting.value = false;
      }
    };

    // 年の桁数を4桁までに制限
    const validateYear = (event) => {
      const input = event.target;
      const value = input.value;

      if (value) {
        // YYYY-MM-DD 形式から年を抽出
        const parts = value.split("-");
        if (parts.length === 3) {
          let year = parts[0];

          // 年が4桁を超えている場合は4桁にトリミング
          if (year.length > 4) {
            year = year.substring(0, 4);
            const newValue = `${year}-${parts[1]}-${parts[2]}`;
            formData.value.deadline = newValue;
            input.value = newValue;
          }
        }
      }
    };

    // モーダルが開かれた時
    watch(
      () => props.isOpen,
      async (isOpen) => {
        if (isOpen && (props.shioriId || props.shareToken)) {
          // テンプレートカテゴリを取得
          await templateStore.fetchTemplateItems();
          // メンバーを取得（shareToken経由でない場合のみ）
          if (!props.shareToken && props.shioriId) {
            await shioriMemberStore.fetchMembers(props.shioriId);
          }

          // 編集モードの場合、データをフォームに反映
          if (props.editMode && props.checklistData) {
            const assignedCodes = (props.checklistData.MEMBERS || []).map(
              (m) => m.ASSIGNEE_USER_CODE,
            );
            if (
              assignedCodes.length === 0 &&
              props.checklistData.ASSIGNED_MEMBER_CODE
            ) {
              assignedCodes.push(props.checklistData.ASSIGNED_MEMBER_CODE);
            }
            formData.value = {
              itemName: props.checklistData.ITEM_NAME || "",
              memoText: props.checklistData.MEMO_TEXT || "",
              categoryName: props.checklistData.CATEGORY_NAME || "",
              assignedMemberCode:
                props.checklistData.ASSIGNED_MEMBER_CODE || "",
              deadline: props.checklistData.DEADLINE || "",
              priorityLevel: props.checklistData.PRIORITY_LEVEL || "NORMAL",
              deadlineNotify:
                !isSharedEditing.value &&
                props.checklistData.DEADLINE_NOTIFY_FLG === "1",
              selectedMembers: assignedCodes,
              isPrivate:
                !isSharedEditing.value &&
                props.checklistData.PRIVATE_FLG === "1",
            };
          } else if (props.copyMode && props.checklistData) {
            const assignedCodes = (props.checklistData.MEMBERS || []).map(
              (m) => m.ASSIGNEE_USER_CODE,
            );
            if (
              assignedCodes.length === 0 &&
              props.checklistData.ASSIGNED_MEMBER_CODE
            ) {
              assignedCodes.push(props.checklistData.ASSIGNED_MEMBER_CODE);
            }
            // コピーモード（非公開フラグは引き継がない）
            formData.value = {
              itemName: props.checklistData.ITEM_NAME || "",
              memoText: props.checklistData.MEMO_TEXT || "",
              categoryName: props.checklistData.CATEGORY_NAME || "",
              assignedMemberCode:
                props.checklistData.ASSIGNED_MEMBER_CODE || "",
              deadline: props.checklistData.DEADLINE || "",
              priorityLevel: props.checklistData.PRIORITY_LEVEL || "NORMAL",
              deadlineNotify:
                !isSharedEditing.value &&
                props.checklistData.DEADLINE_NOTIFY_FLG === "1",
              selectedMembers: assignedCodes,
              isPrivate: false,
            };
          } else if (props.scheduleData) {
            // スケジュールから作成する場合
            const scheduleStart = props.scheduleData.START_YMD;
            let defaultDeadline = "";
            if (scheduleStart) {
              const startDate = new Date(scheduleStart);
              startDate.setDate(startDate.getDate() - 1);
              defaultDeadline = startDate.toISOString().split("T")[0];
            }

            formData.value = {
              itemName: `${props.scheduleData.SUBJECT_NMV}の準備`,
              memoText: "",
              categoryName: "スケジュール準備",
              assignedMemberCode: "",
              deadline: defaultDeadline,
              priorityLevel: "NORMAL",
              deadlineNotify: true,
              selectedMembers: [],
              isPrivate: false,
            };
          } else {
            // 新規作成モード
            resetForm();
            // BELONGINGS の場合、自分をデフォルトで選択
            if (props.itemType === "BELONGINGS") {
              const myMember = shioriMembers.value.find(
                (m) => m.APP_USER_CODE === currentUserCode.value,
              );
              if (myMember) {
                formData.value.selectedMembers = [myMember.MEMBER_CODE];
              }
            }
          }
        }
      },
    );

    return {
      formData,
      errorMessage,
      isSubmitting,
      shioriMembers,
      currentUserCode,
      isSharedEditing,
      existingCategories,
      showCategoryDropdown,
      filteredCategories,
      selectCategory,
      hideCategoryDropdown,
      closeModal,
      handleSubmit,
      validateYear,
    };
  },
};
</script>

<style scoped>
/* モーダルオーバーレイ */
.modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
  padding: 1rem;
  padding-top: 5rem;
  overflow-y: auto;
}

/* モーダルコンテナ */
.modal-content {
  background: white;
  border-radius: var(--radius-xl);
  width: 100%;
  max-width: 28rem;
  max-height: calc(100dvh - 2rem);
  box-shadow: var(--shadow-xl);
  animation: modal-slide-up 0.3s ease-out;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

@keyframes modal-slide-up {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* モーダルヘッダー */
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 1.5rem 1rem;
  border-bottom: 1px solid var(--color-border-light);
}

.modal-header h2 {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--color-text);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.close-button {
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: var(--color-text-muted);
  font-size: 1.75rem;
  line-height: 1;
  cursor: pointer;
  border-radius: var(--radius-md);
  transition: all var(--transition-base);
}

.close-button:hover {
  background-color: var(--color-cool-bg);
  color: var(--color-text);
}

/* エラーメッセージ */
.error-message {
  margin: 1.5rem 1.5rem 0;
  padding: 0.875rem 1rem;
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border-left: 3px solid #ef4444;
  border-radius: var(--radius-md);
  color: #991b1b;
  font-size: 0.875rem;
  font-weight: 500;
}

/* フォーム */
.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.form-group {
  margin-bottom: 1.25rem;
}

.form-group label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 0.5rem;
}

.required {
  color: #ef4444;
}

.form-group input[type="text"],
.form-group input[type="date"],
.form-group textarea,
.form-group select {
  width: 100%;
  padding: 0.625rem 0.875rem;
  font-size: 0.9375rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  transition: all var(--transition-base);
  background-color: white;
  box-sizing: border-box;
}

.form-group input[type="text"]:focus,
.form-group input[type="date"]:focus,
.form-group textarea:focus,
.form-group select:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-group input[type="text"]:hover:not(:focus),
.form-group input[type="date"]:hover:not(:focus),
.form-group textarea:hover:not(:focus),
.form-group select:hover:not(:focus) {
  border-color: var(--color-text-light);
}

.form-group textarea {
  min-height: 96px;
  resize: vertical;
  font-family: inherit;
}

.field-help {
  margin: 0.35rem 0 0;
  color: var(--color-text-muted);
  font-size: 0.75rem;
  text-align: right;
}

/* コンボボックス */
.combobox-wrapper {
  position: relative;
}

.combobox-wrapper input[type="text"] {
  width: 100%;
  padding: 0.625rem 0.875rem;
  font-size: 0.9375rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  transition: all var(--transition-base);
  background-color: white;
  box-sizing: border-box;
}

.combobox-wrapper input[type="text"]:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.combobox-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 10;
  margin-top: 2px;
  max-height: 180px;
  overflow-y: auto;
  list-style: none;
  padding: 0.25rem 0;
  margin-left: 0;
}

.combobox-dropdown li {
  padding: 0.5rem 0.875rem;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background var(--transition-fast);
}

.combobox-dropdown li:hover {
  background: var(--color-background);
  color: var(--primary-color);
}

.checkbox-group label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-weight: 500;
  margin-bottom: 0;
}

.checkbox-group input[type="checkbox"] {
  width: 1.125rem;
  height: 1.125rem;
  cursor: pointer;
  accent-color: var(--primary-color);
}

/* 非公開設定 */
.private-group {
  background-color: #fef9c3;
  border: 1px solid #fde047;
  border-radius: var(--radius-md);
  padding: 0.625rem 0.875rem;
}

.private-label {
  width: 100%;
}

.private-label-text {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  color: #92400e;
  font-size: 0.875rem;
}

.private-icon {
  color: #b45309;
  flex-shrink: 0;
}

/* 非公開時の非活性メンバー行 */
.member-row-disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.member-row-disabled input[type="checkbox"] {
  cursor: not-allowed;
}

.member-private-note {
  font-size: 0.7rem;
  color: #92400e;
  background: #fef9c3;
  border: 1px solid #fde047;
  border-radius: var(--radius-full);
  padding: 0.1rem 0.4rem;
  margin-left: auto;
  white-space: nowrap;
}

/* モーダルフッター */
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.25rem 1.5rem;
  border-top: 1px solid var(--color-border-light);
  flex-shrink: 0;
}

.cancel-button,
.submit-button {
  padding: 0.625rem 1.25rem;
  border-radius: var(--radius-md);
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all var(--transition-base);
}

.cancel-button {
  background-color: var(--color-cool-bg);
  color: var(--color-text-muted);
}

.cancel-button:hover:not(:disabled) {
  background-color: var(--color-border);
  color: var(--color-text);
}

.submit-button {
  background: linear-gradient(
    135deg,
    var(--primary-color) 0%,
    var(--primary-hover) 100%
  );
  color: white;
}

.submit-button:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(180, 207, 158, 0.4);
}

.cancel-button:disabled,
.submit-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* トランジション */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active .modal-content {
  animation: modal-slide-up 0.3s ease-out;
}

.modal-fade-leave-active .modal-content {
  animation: modal-slide-down 0.3s ease-out;
}

@keyframes modal-slide-down {
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
}

/* 持ち物メンバー選択 */
.member-select-list {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: 0.5rem 0.5rem;
  background: var(--color-background);
}

.member-check-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.875rem;
  color: var(--color-text);
  padding: 0.5rem 0.625rem;
  border-radius: var(--radius-md);
  transition: background-color var(--transition-base);
  line-height: 1;
}

.member-check-row:hover {
  background-color: var(--color-cool-bg);
}

.member-check-row input[type="checkbox"] {
  width: 1.125rem;
  height: 1.125rem;
  cursor: pointer;
  accent-color: var(--primary-color);
  flex-shrink: 0;
  margin: 0;
  vertical-align: middle;
}

.member-check-name {
  flex: 1;
  padding-left: 0.25rem;
}

.no-member-note {
  font-size: 0.8rem;
  color: var(--color-text-light);
  margin: 0;
}

/* レスポンシブ */
@media (max-width: 640px) {
  .modal-overlay {
    padding: 0.5rem;
    align-items: flex-start;
  }

  .modal-content {
    max-width: 100%;
    width: 100%;
    max-height: calc(100dvh - 1rem);
  }

  .modal-header {
    padding: 1rem;
  }

  .modal-header h2 {
    font-size: 1.125rem;
  }

  .close-button {
    width: 32px;
    height: 32px;
    font-size: 1.5rem;
  }

  .modal-body {
    padding: 1rem;
  }

  .form-group {
    margin-bottom: 1rem;
  }

  .modal-footer {
    gap: 0.5rem;
    padding: 1rem;
  }

  .cancel-button,
  .submit-button {
    font-size: 0.875rem;
    padding: 0.625rem 1rem;
  }
}
</style>
